import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  CommonNotificationType,
  INotificationBaseMessage,
} from "@evennt-tech/api-v2-core";
import {
  IGetManyProductsBySupplierAndShowQueryReq,
  IProductSupplierShowCategoryWithProducts,
  IProductSupplierShowListItem,
} from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { PRODUCTS_PER_CATEGORY } from "store/product/product.store";
import {
  hasMoreCategoryPages,
  withProductPaging,
} from "store/product/grouped-categories";
import {
  matchesProductChange,
  ProductChangePayload,
  subscribeToProductChanges,
} from "store/product/product-notifications";
import { useShowProducts } from "store/suppliers/show-products";
import { registerStore } from "store/utils/reset-store.utils";

const SURCHARGE_CHANGE_KEY = CommonNotificationType.UPDATE_SURCHARGE;

const subscribeSurchargeChanges = ({
  showId,
  listenerIdPrefix,
  callback,
}: {
  showId?: string;
  listenerIdPrefix: string;
  callback: (payload: ProductChangePayload) => void;
}) => {
  apiV2.notification.subscribe<INotificationBaseMessage<ProductChangePayload>>({
    key: SURCHARGE_CHANGE_KEY,
    ...(showId && { showId }),
    listenerId: `${listenerIdPrefix}_${SURCHARGE_CHANGE_KEY}_${
      showId ? "show" : "common"
    }`,
    callback: ({ payload }) => callback(payload ?? {}),
  });
};

type Pagination = {
  page: number;
  limit: number;
  total: number;
};

type State = {
  products: IProductSupplierShowListItem[];
  pagination: Pagination;
  lastUsedParams: IGetManyProductsBySupplierAndShowQueryReq | null;
  allProductsTotal: number;
  allProductsTotalParams: AllProductsTotalParams | null;
  isLoading: boolean;
  // Raw products grouped by category (server-side pagination is over
  // categories). Expansion into per-show priced products happens in the
  // useSupplierCompanyShowProducts hook.
  groupedProducts: IProductSupplierShowCategoryWithProducts[];
  groupedPagination: Pagination;
  groupedParams: GroupedProductsBySupplierAndShowParams | null;
  groupedLoading: boolean;
  groupedCategoriesLoadingMore: boolean;
  groupedCategoryLoading: Record<string, boolean>;
  groupedCategoryPages: Record<string, number>;
  groupedCategoryLimits: Record<string, number>;
  groupedCategorySearch: Record<string, string>;
};

type Action = {
  getManyBySupplierAndShow: (
    params: IGetManyProductsBySupplierAndShowQueryReq
  ) => Promise<void>;
  getAllProductsTotalBySupplierAndShow: (
    params: AllProductsTotalParams
  ) => Promise<void>;
  getManyBySupplierAndShowGroupedByCategory: (
    params: GroupedProductsBySupplierAndShowParams
  ) => Promise<void>;
  loadMoreGroupedCategories: () => Promise<void>;
  setGroupedCategoryPage: (
    categoryId: string,
    page: number,
    options?: { force?: boolean; limit?: number }
  ) => Promise<void>;
  setGroupedCategorySearch: (
    categoryId: string,
    search: string
  ) => Promise<void>;
  autoCatch: () => void;
  autoCatchGroupedByCategory: () => void;
};

type Store = State & Action;

const onProductsChange = (
  get: () => Store,
  { clearLocalPrices = false }: { clearLocalPrices?: boolean } = {}
) => (payload: ProductChangePayload) => {
  const { lastUsedParams } = get();

  if (!lastUsedParams) return;
  if (!matchesProductChange(payload, lastUsedParams)) return;

  if (clearLocalPrices) {
    useShowProducts.getState().clearPriceWithFees(lastUsedParams.showId);
  }

  get().getManyBySupplierAndShow(lastUsedParams);
};

const onGroupedProductsChange = (
  get: () => Store,
  { clearLocalPrices = false }: { clearLocalPrices?: boolean } = {}
) => (payload: ProductChangePayload) => {
  const { groupedParams, allProductsTotalParams } = get();

  if (!groupedParams) return;
  if (!matchesProductChange(payload, groupedParams)) return;

  if (clearLocalPrices) {
    useShowProducts.getState().clearPriceWithFees(groupedParams.showId);
  }

  get().getManyBySupplierAndShowGroupedByCategory(groupedParams);

  if (allProductsTotalParams) {
    get().getAllProductsTotalBySupplierAndShow(allProductsTotalParams);
  }
};

export type AllProductsTotalParams = {
  showId: string;
  supplierCompanyId: string;
};

export type GroupedProductsBySupplierAndShowParams = Omit<
  IGetManyProductsBySupplierAndShowQueryReq,
  "page" | "limit"
>;

export const useProductSupplierShowStore = create(
  devtools<Store>(
    (set, get) => ({
      products: [],
      pagination: { page: 1, limit: 10, total: 0 },
      lastUsedParams: null,
      allProductsTotal: 0,
      allProductsTotalParams: null,
      isLoading: false,
      groupedProducts: [],
      groupedPagination: { page: 1, limit: 0, total: 0 },
      groupedParams: null,
      groupedLoading: false,
      groupedCategoriesLoadingMore: false,
      groupedCategoryLoading: {},
      groupedCategoryPages: {},
      groupedCategoryLimits: {},
      groupedCategorySearch: {},

      getManyBySupplierAndShow: async (params) => {
        subscribeSurchargeChanges({
          showId: params.showId,
          listenerIdPrefix: "product_supplier_show_store",
          callback: onProductsChange(get, { clearLocalPrices: true }),
        });

        set({ isLoading: true, lastUsedParams: params });

        try {
          const data = await apiV2.product.supplierShow.getMany(params);
          set({
            products: data.items,
            pagination: {
              page: data.meta.currentPage,
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ isLoading: false });
        }
      },

      getAllProductsTotalBySupplierAndShow: async (params) => {
        set({ allProductsTotalParams: params });

        try {
          const data = await apiV2.product.supplierShow.getMany({
            ...params,
            page: 1,
            limit: 1,
          });

          set({ allProductsTotal: data.meta.totalItems ?? 0 });
        } catch (e) {
          console.error(e);
        }
      },

      getManyBySupplierAndShowGroupedByCategory: async (params) => {
        subscribeSurchargeChanges({
          showId: params.showId,
          listenerIdPrefix: "product_supplier_show_grouped_store",
          callback: onGroupedProductsChange(get, { clearLocalPrices: true }),
        });

        set({ groupedLoading: true, groupedParams: params });

        try {
          const data = await apiV2.product.supplierShow.getManyGroupedByCategory(
            withProductPaging(
              { ...params, page: 1 },
              { productsPage: 1, productsLimit: PRODUCTS_PER_CATEGORY }
            )
          );
          set({
            groupedProducts: data.items,
            groupedPagination: {
              page: 1,
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
            groupedCategoryPages: {},
            groupedCategoryLimits: {},
            groupedCategorySearch: {},
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ groupedLoading: false });
        }
      },

      loadMoreGroupedCategories: async () => {
        const {
          groupedParams,
          groupedProducts,
          groupedPagination,
          groupedLoading,
          groupedCategoriesLoadingMore,
        } = get();

        if (
          !groupedParams ||
          !groupedProducts.length ||
          groupedLoading ||
          groupedCategoriesLoadingMore ||
          !hasMoreCategoryPages(groupedPagination)
        ) {
          return;
        }

        const nextPage = groupedPagination.page + 1;

        set({ groupedCategoriesLoadingMore: true });

        try {
          const data = await apiV2.product.supplierShow.getManyGroupedByCategory(
            withProductPaging(
              { ...groupedParams, page: nextPage },
              { productsPage: 1, productsLimit: PRODUCTS_PER_CATEGORY }
            )
          );

          set((state) => {
            const loadedIds = new Set(
              state.groupedProducts.map(({ id }) => id)
            );

            return {
              groupedProducts: [
                ...state.groupedProducts,
                ...data.items.filter(({ id }) => !loadedIds.has(id)),
              ],
              groupedPagination: {
                page: nextPage,
                limit: data.meta.itemsPerPage,
                total: data.meta.totalItems ?? state.groupedPagination.total,
              },
            };
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ groupedCategoriesLoadingMore: false });
        }
      },

      setGroupedCategoryPage: async (categoryId, page, options) => {
        const {
          groupedParams,
          groupedCategoryLoading,
          groupedCategoryLimits,
          groupedCategorySearch,
        } = get();

        if (!groupedParams) return;
        if (groupedCategoryLoading[categoryId] && !options?.force) return;

        const search = groupedCategorySearch[categoryId] ?? "";
        const limit =
          options?.limit ??
          groupedCategoryLimits[categoryId] ??
          PRODUCTS_PER_CATEGORY;

        set((state) => ({
          groupedCategoryLoading: {
            ...state.groupedCategoryLoading,
            [categoryId]: true,
          },
        }));

        try {
          // The grouped endpoint paginates over categories, so a single
          // category's products are paged via the flat by-supplier-and-show
          // endpoint filtered by this category id.
          const data = await apiV2.product.supplierShow.getMany({
            ...groupedParams,
            categoryIds: categoryId,
            search: search || undefined,
            page,
            limit,
          });

          // A newer term for this category superseded this request while it
          // was in flight — its rows would contradict the input.
          if ((get().groupedCategorySearch[categoryId] ?? "") !== search) {
            return;
          }

          set((state) => ({
            groupedProducts: state.groupedProducts.map((category) =>
              category.id === categoryId
                ? {
                    ...category,
                    products: data.items,
                    totalProducts:
                      data.meta.totalItems ?? category.totalProducts,
                    hasMore: (data.meta.totalItems ?? 0) > page * limit,
                  }
                : category
            ),
            groupedCategoryPages: {
              ...state.groupedCategoryPages,
              [categoryId]: page,
            },
            groupedCategoryLimits: {
              ...state.groupedCategoryLimits,
              [categoryId]: limit,
            },
          }));
        } catch (e) {
          console.error(e);
        } finally {
          set((state) => ({
            groupedCategoryLoading: {
              ...state.groupedCategoryLoading,
              [categoryId]: false,
            },
          }));
        }
      },

      setGroupedCategorySearch: async (categoryId, search) => {
        if ((get().groupedCategorySearch[categoryId] ?? "") === search) return;

        set((state) => ({
          groupedCategorySearch: {
            ...state.groupedCategorySearch,
            [categoryId]: search,
          },
        }));

        await get().setGroupedCategoryPage(categoryId, 1, { force: true });
      },

      autoCatch: () => {
        subscribeToProductChanges({
          listenerIdPrefix: "product_supplier_show_store",
          callback: onProductsChange(get),
        });

        subscribeSurchargeChanges({
          listenerIdPrefix: "product_supplier_show_store",
          callback: onProductsChange(get, { clearLocalPrices: true }),
        });
      },

      autoCatchGroupedByCategory: () => {
        subscribeToProductChanges({
          listenerIdPrefix: "product_supplier_show_grouped_store",
          callback: onGroupedProductsChange(get),
        });

        subscribeSurchargeChanges({
          listenerIdPrefix: "product_supplier_show_grouped_store",
          callback: onGroupedProductsChange(get, { clearLocalPrices: true }),
        });
      },
    }),
    { name: "product-supplier-show" }
  )
);

registerStore(useProductSupplierShowStore);
